import { InsightsView, useToast } from '@elva/ui';
import type { InsightCheck, InsightView } from '@elva/ui';
import { useWorkspaceLabel } from '@elva/platform';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { keys } from '@/api/queryKeys';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { adaptInsightChecks, insightChecksToApi, insightViewsRecord } from '../adapters/settingsAdapters';
import { settingsApi } from '../api/settingsApi';
import { useCalendarInsightsViews } from '../hooks/useCalendarInsights';

const VIEWS: InsightView[] = ['daily', 'weekly', 'monthly'];

/** Appointment & booking › Calendar insights (spec 15 §10). Stage's checks, ticks from the views API. */
export function SettingsCalendarInsightsPage() {
  const workspaceLabel = useWorkspaceLabel();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useCalendarInsightsViews();
  const checks = useMemo(() => adaptInsightChecks(data?.views ?? {}), [data?.views]);
  const ready = !isLoading && !isError;
  const saved = useRef<InsightCheck[] | null>(null);
  if (ready && saved.current == null) saved.current = checks;

  return (
    <ElvaSectionState isLoading={isLoading} isError={isError}>
      <InsightsView
        checks={checks}
        location={workspaceLabel}
        onChange={(next) => {
          const record = insightViewsRecord(data?.views ?? {});
          const previous = saved.current ?? checks;
          saved.current = next;
          for (const view of VIEWS) {
            if (enabledSignature(previous, view) === enabledSignature(next, view)) continue;
            void settingsApi.updateCalInsightsView(view, insightChecksToApi(view, next, record[view])).then(
              () => {
                void queryClient.invalidateQueries({ queryKey: keys.settings.calInsights() });
              },
              () => {
                toast({ title: 'Could not save calendar insights', variant: 'error' });
              },
            );
          }
        }}
      />
    </ElvaSectionState>
  );
}

function enabledSignature(checks: InsightCheck[], view: InsightView): string {
  return checks
    .filter((check) => check.view === view)
    .map((check) => (check.enabled ? '1' : '0'))
    .join('');
}
